import React, { useState, useEffect } from "react";
import {
  getLanguageString as _,
  getCurrentLanguage,
  setLanguage,
} from "misc/lang";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import css from "./loginScreen.module.scss";
import TextInput from "../../components/form/TextInput";
import { validateEmailAddress } from "misc/validators";
import { DefaultAndAuthProps } from "model/props";
import FormButton from "../../components/form/FormButton";
import { signInWithEmailPassword, triggerPasswordReset } from "misc/auth_utils";
import BackgroundImage from "../../assets/images/login1.png";
import ForgotPwBackgroundImage from "../../assets/images/forgotPw.png";
import LogoImage from "../../assets/images/addyou_white.png";

const LoginScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { loader } = props;

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const [modeForgotPassword, setModeForgotPassword] = useState<boolean>(false);
  const [currentLang, setCurrentLang] = useState<string>(getCurrentLanguage());

  const isValidEmail = !validateEmailAddress(email);
  const canLogin = isValidEmail && !!password;

  useEffect(() => {
    setError("");
  }, [modeForgotPassword]);

  /////////////////////////////////////////////////////////////////////////

  const actionLogin = (event: any) => {
    event.preventDefault();
    if (!canLogin) return;

    signInWithEmailPassword(email, password, loader, props, setError);
  };

  const actionTriggerPasswordReset = (event: any) => {
    event.preventDefault();
    if (!email) return;

    triggerPasswordReset(email, loader, setError);
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    setCurrentLang(lang);
    // Force re-render to update all translations
    window.location.reload();
  };

  /////////////////////////////////////////////////////////////////////////

  return (
    <div className={css.container}>
      <div className={css.form}>
        <div className={css.logo}>
          <img src={LogoImage} alt="AddYou Logo" />
        </div>
        <form onSubmit={actionLogin}>
          {modeForgotPassword && (
            <>
              <div className={css.heading}>
                {_("auth", "forgot_password_head")}
              </div>
              <div className={css.info}>
                {_("auth", "forgot_password_info")}
              </div>
            </>
          )}
          {!modeForgotPassword && (
            <>
              <div className={css.headingContainer}>
                <span className={css.heading}>{_("auth", "login_head")}</span>
                {!modeForgotPassword && (
                  <div className={css.languageSelector}>
                    <select
                      className={css.langDropdown}
                      value={currentLang}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                      <option value="sv">🇸🇪 Svenska</option>
                      <option value="en">🇬🇧 English</option>
                    </select>
                  </div>
                )}
              </div>
              <div className={css.info}>{_("auth", "login_info")}</div>
            </>
          )}

          <TextInput
            autocomplete="email"
            label={_("general", "email")}
            initialValue={email}
            updateValue={setEmail}
            validator={validateEmailAddress}
          />

          {modeForgotPassword && (
            <>
              <FormButton
                label={_("auth", "btn_trigger_password_reset")}
                onSubmit={actionTriggerPasswordReset}
                disabled={!email}
              />
              <div
                className={css.backLink}
                onClick={() => {
                  setModeForgotPassword(false);
                }}
              >
                {_("general", "btn_cancel")}
              </div>
            </>
          )}

          {!modeForgotPassword && (
            <>
              <TextInput
                autocomplete="current-password"
                type={showPassword ? "text" : "password"}
                label={_("general", "password")}
                initialValue={password}
                updateValue={setPassword}
                endIcon={showPassword ? <VisibilityOff /> : <Visibility />}
                iconAction={() => setShowPassword(!showPassword)}
              />
              <FormButton
                label={_("auth", "btn_login")}
                onSubmit={actionLogin}
                disabled={!canLogin}
              />
              <div className={css.rememberForgotRow}>
                <label className={css.rememberMe}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <span
                  className={css.forgotPassword}
                  onClick={() => setModeForgotPassword(true)}
                >
                  {_("auth", "btn_forgot_password")}
                </span>
              </div>
            </>
          )}
        </form>
        {error && <div className={css.error}>{error}</div>}
      </div>
      <div className={css.backgroundContainer}>
        <img
          alt="background"
          src={modeForgotPassword ? ForgotPwBackgroundImage : BackgroundImage}
          className={css.bgImage}
        ></img>
      </div>
    </div>
  );
};

export default LoginScreen;
